import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from "recharts";
import techniciansService from "../../../api/techniciansService";
import qualityService from "../../../api/qualityService";
import { getDefaultEvaluationPeriod } from "../../../utils/evaluationPeriod";
import { C } from "../theme";
import { TECHNICIAN_STATUS } from "../constants";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const YEARS = [2026, 2025, 2024];

const SCORE_DIMENSIONS = [
  { key: "serviceQuality", label: "Calidad", color: "#6366f1" },
  { key: "punctuality", label: "Puntualidad", color: "#0ea5e9" },
  { key: "attitude", label: "Actitud", color: "#10b981" },
  { key: "reportCompliance", label: "Reportes", color: "#f59e0b" },
  { key: "adminCompliance", label: "Cumpl. Admin", color: "#8b5cf6" },
];

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${C.border}`,
        borderRadius: 1.5,
        p: 1,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text }}>
        {item?.label}: {item?.value}/100
      </Typography>
    </Box>
  );
}

async function loadScoresForTechnician(technician, year, month) {
  const rankingRes = await techniciansService.getRanking(year, month);
  const rankingData = rankingRes.data.data || rankingRes.data;
  const entry = (rankingData?.ranking || []).find(
    (r) => r.technicianId === String(technician._id)
  );

  if (entry?.scores) {
    return {
      scores: entry.scores,
      total: entry.total ?? 0,
      source: "ranking",
    };
  }

  const qualityRes = await qualityService.getByPeriod(year, month);
  const evals = qualityRes.data.data || [];
  const evalDoc = evals.find(
    (e) => String(e.technician?._id || e.technician) === String(technician._id)
  );

  const scores = {
    serviceQuality: evalDoc?.serviceQualityScore ?? 0,
    punctuality: evalDoc?.punctualityScore ?? 0,
    attitude: technician.attitudeScore ?? 0,
    reportCompliance: 0,
    adminCompliance: technician.adminComplianceScore ?? 0,
  };

  const total =
    Math.round(
      (scores.serviceQuality * 0.25 +
        scores.punctuality * 0.2 +
        scores.attitude * 0.2 +
        scores.reportCompliance * 0.2 +
        scores.adminCompliance * 0.15) *
        10
    ) / 10;

  return { scores, total, source: "partial" };
}

export default function TechnicianScoresChart({ technician, fillHeight = false }) {
  const defaultPeriod = getDefaultEvaluationPeriod();
  const [year, setYear] = useState(defaultPeriod.year);
  const [month, setMonth] = useState(defaultPeriod.month);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scoreData, setScoreData] = useState(null);

  useEffect(() => {
    if (!technician?._id) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await loadScoresForTechnician(technician, year, month);
        if (active) setScoreData(result);
      } catch (err) {
        console.error("Error cargando calificaciones", err);
        if (active) setError("No se pudieron cargar las calificaciones");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [technician?._id, year, month]);

  const chartData = useMemo(() => {
    if (!scoreData?.scores) return [];
    return SCORE_DIMENSIONS.map((dim) => ({
      key: dim.key,
      label: dim.label,
      value: scoreData.scores[dim.key] ?? 0,
      color: dim.color,
    }));
  }, [scoreData]);

  const isInactive = technician?.status === TECHNICIAN_STATUS.INACTIVE;

  return (
    <Box
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        bgcolor: "#fff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...(fillHeight ? { flex: 1, minHeight: 0, height: "100%" } : {}),
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          borderBottom: `1px solid ${C.border}`,
          bgcolor: "#fafafa",
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.text }}>
            Calificaciones · {MONTHS[month - 1]} {year}
          </Typography>
          {scoreData?.total != null && (
            <Typography sx={{ fontSize: 11, color: C.textSoft }}>
              Total ponderado: <strong>{scoreData.total}</strong> pts
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 0.75 }}>
          <FormControl size="small" sx={{ minWidth: 96 }}>
            <Select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              sx={{ fontSize: 12, bgcolor: "#fff", height: 32 }}
            >
              {MONTHS.map((m, i) => (
                <MenuItem key={m} value={i + 1} sx={{ fontSize: 13 }}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 72 }}>
            <Select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ fontSize: 12, bgcolor: "#fff", height: 32 }}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {(isInactive || scoreData?.source === "partial") && !loading && (
        <Typography
          sx={{
            fontSize: 11,
            color: isInactive ? C.warning : C.textSoft,
            px: 1.5,
            py: 0.5,
            flexShrink: 0,
            bgcolor: isInactive ? "#fffbeb" : "transparent",
          }}
        >
          {isInactive
            ? "Inactivo — no participa en el ranking."
            : "Datos parciales (el cumplimiento de reportes solo aparece en el ranking)."}
        </Typography>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: fillHeight ? 0 : 160,
          p: 1,
          display: "flex",
          alignItems: "center",
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} sx={{ color: C.primary }} />
          </Box>
        ) : error ? (
          <Typography sx={{ fontSize: 12, color: C.danger, textAlign: "center", width: "100%" }}>
            {error}
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis
                type="number"
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: C.textSoft }}
                axisLine={{ stroke: C.border }}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={88}
                tick={{ fontSize: 11, fill: C.text }}
                axisLine={false}
                tickLine={false}
              />
              <ReferenceLine x={70} stroke="#d1d5db" strokeDasharray="4 4" />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(25,117,209,0.04)" }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
                {chartData.map((entry) => (
                  <Cell key={entry.key} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
}
